// ============================================================
// RULE-BASED RUBRIC FOR TOEFL iBT (2026) WRITING TASKS
// Write an Email and Academic Discussion are each scored 0–5, in
// half-point steps, from a handful of transparent criteria. Each
// criterion returns diagnostics the result page shows as feedback.
// ============================================================

const roundHalf = (n) => Math.round(n * 2) / 2;
const clamp = (n, lo = 0, hi = 5) => Math.min(hi, Math.max(lo, n));

const tokenize = (text) => (text.toLowerCase().match(/[a-z']+/g) || []);
const sentencesOf = (text) =>
  text
    .replace(/([.!?])\s+/g, "$1\n")
    .split(/\n+/)
    .map((s) => s.trim())
    .filter((s) => /[a-z]/i.test(s));

const STOPWORDS = new Set(
  "a an the and or but if of to in on at for with by from as is are was were be been being it its this that these those i you he she we they me him her us them my your his our their do does did have has had not no so than too very can could will would should may might must just also there here what which who whom when where why how all any some each more most such only own same into about over after before again then once".split(" ")
);

const CONNECTORS = [
  "because", "however", "therefore", "for example", "for instance", "such as", "in addition",
  "moreover", "although", "while", "since", "as a result", "on the other hand", "first",
  "second", "finally", "also", "furthermore", "in my opinion", "this means", "instead",
];

const COMMON_MISSPELLINGS = {
  recieve: "receive", becuase: "because", beacuse: "because", definately: "definitely",
  untill: "until", wich: "which", alot: "a lot", thier: "their", occured: "occurred",
  seperate: "separate", tommorow: "tomorrow", goverment: "government", enviroment: "environment",
  wierd: "weird", truely: "truly", begining: "beginning", beleive: "believe", freind: "friend",
  oppinion: "opinion", studing: "studying", writting: "writing", acomodate: "accommodate",
};

// Frequent learner grammar/usage errors: [pattern, explanation]
const GRAMMAR_PATTERNS = [
  [/\b(he|she|it) don't\b/gi, "subject–verb agreement: use \"doesn't\""],
  [/\b(could|should|would|must) of\b/gi, "use \"could have / should have\", not \"of\""],
  [/\b(more|most) (better|worse|easier|harder|best|worst)\b/gi, "double comparative"],
  [/\b(informations|advices|equipments|researches|feedbacks|knowledges|evidences)\b/gi, "uncountable noun with -s"],
  [/\bpeoples\b(?! of)/gi, "\"people\" is already plural"],
  [/\b(discuss|emphasi[sz]e|mention) about\b/gi, "this verb takes no \"about\""],
  [/\bdespite of\b/gi, "use \"despite\" or \"in spite of\""],
  [/\bdid(n't| not) (went|came|saw|did|made|took|gave|got|had|was)\b/gi, "use the base form after \"did\""],
  [/\b(can|could|will|would|should|must|may|might) (to )?(went|goes|going|wanted|needs)\b/gi, "use the base form after a modal verb"],
  [/\b(is|are|was|were) (consist|depend|belong)\b/gi, "these verbs are not used in the passive"],
  [/\bthe (both|most of)\b/gi, "drop \"the\""],
];

const countPhrase = (lower, phrase) => {
  const re = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
  return (lower.match(re) || []).length;
};

// ── Language use (shared by both tasks) ─────────────────────────
function scoreLanguage(text) {
  const diagnostics = [];
  const words = tokenize(text);
  const wc = words.length;
  if (wc === 0) return { score: 0, diagnostics: ["No response was written."] };

  const sentences = sentencesOf(text);
  let errors = 0;

  const lowerStarts = sentences.filter((s) => /^[a-z]/.test(s)).length;
  if (lowerStarts > 0) {
    errors += lowerStarts;
    diagnostics.push(`${lowerStarts} sentence(s) begin with a lowercase letter.`);
  }

  const lowerI = (text.match(/(^|\s)i(\s|'|,|\.|$)/g) || []).length;
  if (lowerI > 0) {
    errors += lowerI;
    diagnostics.push(`The pronoun "I" is written in lowercase ${lowerI} time(s).`);
  }

  const doubled = (text.toLowerCase().match(/\b(\w+)\s+\1\b/g) || []).filter((m) => !/^(that that|had had)$/.test(m));
  if (doubled.length > 0) {
    errors += doubled.length;
    diagnostics.push(`Repeated word(s): "${doubled[0]}".`);
  }

  const articleErrors = (text.match(/\ba\s+[aeiou]\w+/gi) || []).filter((m) => !/\ba\s+(uni|use|usu|euro|one|once|u\w*al)/i.test(m));
  if (articleErrors.length > 0) {
    errors += articleErrors.length;
    diagnostics.push(`Check article use: "${articleErrors[0]}" should probably use "an".`);
  }

  const misspelled = words.filter((w) => COMMON_MISSPELLINGS[w]);
  if (misspelled.length > 0) {
    errors += misspelled.length;
    diagnostics.push(`Spelling: "${misspelled[0]}" → "${COMMON_MISSPELLINGS[misspelled[0]]}".`);
  }

  const grammarHits = GRAMMAR_PATTERNS.flatMap(([re, why]) => (text.match(re) || []).map((m) => [m, why]));
  if (grammarHits.length > 0) {
    errors += grammarHits.length;
    diagnostics.push(`Grammar: "${grammarHits[0][0]}" — ${grammarHits[0][1]}.${grammarHits.length > 1 ? ` (${grammarHits.length} such errors)` : ""}`);
  }

  // Padding: the same sentence written more than once.
  const normalized = sentences.map((s) => s.toLowerCase().replace(/[^a-z ]/g, "").trim()).filter((s) => s.split(" ").length >= 4);
  const repeats = normalized.length - new Set(normalized).size;
  if (repeats > 0) {
    errors += repeats * 2;
    diagnostics.push(`${repeats} sentence(s) are repeated word for word — repetition does not add development.`);
  }

  // An email legitimately ends with a sign-off line and a name
  // ("Best regards,\nAlex"), so check the punctuation before it.
  const body = text
    .trim()
    .replace(/\n\s*(kind |best |warm |warmest |yours )?(sincerely|regards|wishes|best|thanks|thank you|yours truly|respectfully)\b[^\n]*(\n[^\n]{0,40})?$/i, "")
    .trim();
  if (!/[.!?]["')]?\s*$/.test(body || text.trim())) {
    errors += 1;
    diagnostics.push("The response does not end with punctuation.");
  }

  const runOns = sentences.filter((s) => s.split(/\s+/).length > 40).length;
  if (runOns > 0) {
    errors += runOns;
    diagnostics.push(`${runOns} very long sentence(s) — consider splitting them.`);
  }

  // Vocabulary range: distinct content words relative to all content words
  const content = words.filter((w) => !STOPWORDS.has(w) && w.length > 2);
  const ttr = content.length ? new Set(content).size / content.length : 0;
  const longWords = content.filter((w) => w.length >= 8).length;

  // Sentence variety
  const lengths = sentences.map((s) => s.split(/\s+/).length);
  const mean = lengths.reduce((a, b) => a + b, 0) / (lengths.length || 1);
  const sd = Math.sqrt(lengths.reduce((a, b) => a + (b - mean) ** 2, 0) / (lengths.length || 1));

  const errorRate = errors / Math.max(wc / 100, 0.5); // errors per 100 words
  let score = 5 - errorRate * 0.6;

  if (ttr < 0.5) {
    score -= 0.75;
    diagnostics.push("Vocabulary is repetitive — vary your word choice.");
  } else if (ttr > 0.7 && longWords >= 3) {
    score += 0.25;
  }
  if (lengths.length >= 3 && sd < 2.5) {
    score -= 0.5;
    diagnostics.push("Most sentences are a similar length — mix short and complex sentences.");
  }
  if (mean < 7 && lengths.length >= 3) {
    score -= 0.5;
    diagnostics.push("Sentences are very short — combine ideas with connectors such as \"because\" or \"although\".");
  }

  if (diagnostics.length === 0) diagnostics.push("Clear, accurate language with good variety.");
  return { score: clamp(roundHalf(score)), diagnostics };
}

// ── Copying from the prompt ─────────────────────────────────────
function copiedRatio(text, source, n = 5) {
  const a = tokenize(text);
  if (a.length < n) return 0;
  const grams = new Set();
  const b = tokenize(source);
  for (let i = 0; i + n <= b.length; i++) grams.add(b.slice(i, i + n).join(" "));
  let hits = 0;
  for (let i = 0; i + n <= a.length; i++) if (grams.has(a.slice(i, i + n).join(" "))) hits += 1;
  return hits / (a.length - n + 1);
}

function lengthScore(wc, steps) {
  for (const [min, score] of steps) if (wc >= min) return score;
  return 0;
}

function finalize(criteria, weights, wc, copyRatio, minWords) {
  let overall = criteria.reduce((sum, c, i) => sum + c.score * weights[i], 0);
  const warnings = [];

  if (wc < minWords / 3) overall = Math.min(overall, 1);
  if (copyRatio >= 0.35) {
    overall = Math.min(overall, 1.5);
    warnings.push("⚠️ Much of the response repeats wording from the task. Write in your own words.");
  } else if (copyRatio >= 0.2) {
    overall = Math.min(overall, 3);
    warnings.push("⚠️ Parts of the response copy wording from the task, which lowers the score.");
  }
  if (wc < minWords) {
    warnings.push(`⚠️ ${wc} words — aim for at least ${minWords} words to fully develop your response.`);
  }

  return { score: clamp(roundHalf(overall)), criteria, wordCount: wc, warnings };
}

// A response that skips a required part of the task cannot reach the top
// of the scale, however well the rest is written.
function capForMissing(result, missing, what) {
  if (missing === 0 || result.wordCount === 0) return result;
  const cap = missing === 1 ? 3 : 2;
  if (result.score <= cap) return result;
  return {
    ...result,
    score: cap,
    warnings: [...result.warnings, `⚠️ ${missing} required ${what}${missing > 1 ? "s were" : " was"} not addressed, so the score is capped at ${cap}/5.`],
  };
}

// Content words in the response that do not appear anywhere in the prompt
// or classmates' posts — a rough measure of how much the writer adds.
function noveltyRatio(text, source) {
  const seen = new Set(tokenize(source));
  const content = tokenize(text).filter((w) => !STOPWORDS.has(w) && w.length > 2);
  if (content.length === 0) return 0;
  return content.filter((w) => !seen.has(w)).length / content.length;
}

// Concrete support: examples, figures, named cases, personal experience.
const SPECIFIC_MARKERS = /\b(for example|for instance|such as|in my (country|city|town|experience|hometown)|when i|last (year|semester|week|month)|a study|research shows|according to|in \d{4}|\d+ ?(%|percent|hours|weeks|students|years))\b/i;

// ============================================================
// WRITE AN EMAIL
// Optional task fields (used by harder mocks):
//   formal      – recipient expects formal register
//   minWords    – target length (default 80)
//   lengthSteps – [[minWords, score], ...] for Development
// ============================================================
export function scoreEmail(text, task) {
  const lower = text.toLowerCase();
  const wc = tokenize(text).length;
  const sentences = sentencesOf(text);
  const minWords = task.minWords || 80;

  // Task: did the email address all three bullet points — with substance?
  const taskDiag = [];
  let covered = 0;
  let missing = 0;
  task.pointKeywords.forEach((keywords, i) => {
    const hitWords = keywords.filter((k) => countPhrase(lower, k) > 0);
    // Words in the sentences that actually carry this point.
    const depth = sentences
      .filter((s) => hitWords.some((k) => countPhrase(s.toLowerCase(), k) > 0))
      .reduce((n, s) => n + s.split(/\s+/).length, 0);
    if (hitWords.length >= 2 && depth >= 12) covered += 1;
    else if (hitWords.length >= 2) {
      covered += 0.75;
      taskDiag.push(`Point ${i + 1} ("${task.points[i]}") is mentioned but needs a fuller explanation.`);
    } else if (hitWords.length === 1) {
      covered += 0.4;
      taskDiag.push(`Point ${i + 1} ("${task.points[i]}") is only briefly addressed.`);
    } else {
      missing += 1;
      taskDiag.push(`Point ${i + 1} ("${task.points[i]}") is missing.`);
    }
  });
  if (taskDiag.length === 0) taskDiag.push("All three points are addressed with supporting detail.");
  const taskScore = wc === 0 ? 0 : clamp(roundHalf((covered / task.points.length) * 5));

  // Development: enough detail for each point
  const devDiag = [];
  let devScore = lengthScore(wc, task.lengthSteps || [[110, 5], [90, 4.5], [70, 3.5], [50, 2.5], [30, 1.5], [1, 0.5]]);
  if (wc >= minWords) devDiag.push("Good level of detail.");
  else devDiag.push(`${wc} words — add specific details (reasons, examples, consequences) to develop each point.`);
  if (task.formal && wc > 0 && !SPECIFIC_MARKERS.test(text)) {
    devScore -= 0.5;
    devDiag.push("Add at least one concrete detail or example (a number, a specific case, a consequence).");
  }

  // Email conventions & register
  const convDiag = [];
  let conv = 1;
  const formalGreeting = /^\s*(dear|good (morning|afternoon|evening))\b/i.test(text);
  const casualGreeting = /^\s*(hello|hi|hey)\b/i.test(text);
  if (formalGreeting || (casualGreeting && !task.formal)) conv += 1.5;
  else if (casualGreeting) {
    conv += 0.5;
    convDiag.push(`"Hi/Hello" is too casual here — use "Dear ${task.recipient},".`);
  } else convDiag.push(`Start with a greeting, e.g. "Dear ${task.recipient},".`);
  if (/(sincerely|regards|best wishes|best,|thank you|thanks|yours truly|kind regards|respectfully)[\s\S]{0,40}$/i.test(text.trim())) conv += 1.5;
  else convDiag.push("End with a closing, e.g. \"Best regards,\" followed by your name.");
  if (/\b(please|would|could|appreciate|grateful|thank)\b/i.test(text)) conv += 1;
  else convDiag.push("Use polite phrases such as \"I would appreciate it if...\".");
  const slang = lower.match(/\b(u|ur|gonna|wanna|lol|pls|thx|omg|gotta|kinda|yeah|hey)\b/g);
  if (slang) {
    conv -= 1;
    convDiag.push(`Avoid informal language ("${slang[0]}").`);
  }
  if (task.formal) {
    const contractions = (lower.match(/\b\w+'(t|re|ve|ll|d|m)\b/g) || []).length;
    if (contractions > 3) {
      conv -= 0.5;
      convDiag.push(`${contractions} contractions — a formal email to a professor reads better with full forms ("I would", "do not").`);
    }
    const exclaims = (text.match(/!/g) || []).length;
    if (exclaims > 1) {
      conv -= 0.5;
      convDiag.push("Too many exclamation marks for a formal email.");
    }
    if (/\b(you (must|have to|need to)|this is (ridiculous|stupid|unfair))\b/i.test(text)) {
      conv -= 0.5;
      convDiag.push("Some phrasing sounds demanding — soften requests (\"Would it be possible to...\").");
    }
  }
  if (convDiag.length === 0) convDiag.push("Appropriate greeting, closing and polite tone.");

  const criteria = [
    { label: "Task Completion", score: taskScore, max: 5, diagnostics: taskDiag },
    { label: "Development", score: clamp(devScore), max: 5, diagnostics: devDiag },
    { label: "Language Use", max: 5, ...scoreLanguage(text) },
    { label: "Email Conventions & Tone", score: wc === 0 ? 0 : clamp(roundHalf(conv)), max: 5, diagnostics: convDiag },
  ];

  const copy = copiedRatio(text, `${task.scenario} ${task.points.join(" ")}`);
  return capForMissing(finalize(criteria, [0.35, 0.2, 0.3, 0.15], wc, copy, minWords), missing, "bullet point");
}

// ============================================================
// ACADEMIC DISCUSSION
// Optional task fields (used by harder mocks):
//   questionParts – [{ label, keywords }] each part must be answered
//   minWords      – target length (default 100)
//   lengthSteps   – [[minWords, score], ...] for Development
// ============================================================
export function scoreDiscussion(text, task) {
  const lower = text.toLowerCase();
  const words = tokenize(text);
  const wc = words.length;
  const minWords = task.minWords || 100;
  const source = `${task.question} ${task.posts.map((p) => p.text).join(" ")}`;

  // Task: clear opinion, on topic, engages with classmates
  const taskDiag = [];
  let t = 0;
  if (/\b(i (strongly |firmly )?(think|believe|feel|agree|disagree|support|oppose|maintain|would argue)|in my (opinion|view)|from my perspective|personally|my (position|view) is)\b/.test(lower)) t += 1.5;
  else taskDiag.push("State your own position clearly, e.g. \"In my opinion, ...\".");

  const topicHits = new Set(words.filter((w) => task.topicKeywords.includes(w))).size;
  if (topicHits >= 4) t += 2;
  else if (topicHits >= 2) {
    t += 1;
    taskDiag.push("Connect your answer more closely to the professor's question.");
  } else taskDiag.push("The response does not seem to address the professor's question.");

  const names = task.posts.map((p) => p.name.toLowerCase());
  if (names.some((n) => lower.includes(n)) || /\b(classmate|as .{1,20} (said|mentioned|pointed out))\b/.test(lower)) t += 1.5;
  else taskDiag.push(`Engage with the discussion — respond to ${task.posts.map((p) => p.name).join(" or ")}'s point.`);

  // Multi-part questions: every part must be answered.
  let missingPartCount = 0;
  if (task.questionParts) {
    let parts = 0;
    let missingParts = 0;
    task.questionParts.forEach((part) => {
      const hits = part.keywords.filter((k) => countPhrase(lower, k) > 0).length;
      if (hits >= 2) parts += 1;
      else if (hits === 1) {
        parts += 0.5;
        taskDiag.push(`"${part.label}" is only touched on — develop it explicitly.`);
      } else {
        missingParts += 1;
        taskDiag.push(`The question asks you to: ${part.label.toLowerCase()}. This is missing.`);
      }
    });
    t = t * 0.6 + (parts / task.questionParts.length) * 2;
    missingPartCount = missingParts;
  }

  // Contribution: a strong post adds ideas, not just echoes classmates.
  const novelty = noveltyRatio(text, source);
  if (wc >= 40 && novelty < 0.3) {
    t -= 1;
    taskDiag.push("Most of your ideas repeat the professor's or classmates' points — add a new reason or example of your own.");
  }
  if (taskDiag.length === 0) taskDiag.push("Clear position that contributes a new idea to the discussion.");

  // Development: length + reasons/examples
  const devDiag = [];
  let dev = lengthScore(wc, task.lengthSteps || [[120, 5], [100, 4.5], [80, 3.5], [60, 2.5], [40, 1.5], [1, 0.5]]);
  const support = CONNECTORS.filter((c) => countPhrase(lower, c) > 0).length;
  if (wc > 0 && support === 0) {
    dev -= 1;
    devDiag.push("Support your opinion with reasons and examples (\"because\", \"for example\").");
  } else if (support >= 3 && wc >= minWords) {
    devDiag.push("Ideas are well supported with reasons and connectors.");
  }
  if (task.questionParts && wc > 0 && !SPECIFIC_MARKERS.test(text)) {
    dev -= 0.5;
    devDiag.push("No specific example — illustrate your argument with a concrete case, figure or experience.");
  }
  if (wc < minWords) devDiag.push(`${wc} words — aim for at least ${minWords} words.`);
  if (devDiag.length === 0) devDiag.push("Good development; add a specific example to make it stronger.");

  const criteria = [
    { label: "Relevance & Contribution", score: wc === 0 ? 0 : clamp(roundHalf(t)), max: 5, diagnostics: taskDiag },
    { label: "Development & Support", score: clamp(roundHalf(dev)), max: 5, diagnostics: devDiag },
    { label: "Language Use", max: 5, ...scoreLanguage(text) },
  ];

  return capForMissing(finalize(criteria, [0.35, 0.3, 0.35], wc, copiedRatio(text, source), minWords), missingPartCount, "part of the question");
}
